// Global status banner: a colored strip pinned to the top of the app that
// replaces the native browser alert() for status messages.
//
// Color follows the category:
//   error   -> red
//   warning -> yellow
//   success -> green
//   info    -> blue (default)
//
// Framework-agnostic (plain DOM) so it can be called from the Axios
// interceptors in main.js, which run outside any Vue component context.

const CATEGORY_STYLES = {
    error: { bg: '#d32f2f', fg: '#ffffff' },
    warning: { bg: '#f9a825', fg: '#1a1a1a' },
    success: { bg: '#2e7d32', fg: '#ffffff' },
    info: { bg: '#1565c0', fg: '#ffffff' },
};

const CONTAINER_ID = 'global-status-banner';
let hideTimer = null;

function ensureContainer() {
    let el = document.getElementById(CONTAINER_ID);
    if (el) return el;

    el = document.createElement('div');
    el.id = CONTAINER_ID;
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'assertive');
    Object.assign(el.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2147483647',            // above everything, including modals
        padding: '12px 44px 12px 16px',
        textAlign: 'center',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '1.4',
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
        transform: 'translateY(-100%)',
        transition: 'transform 0.25s ease',
        cursor: 'default',
    });

    const msg = document.createElement('span');
    msg.className = 'gsb-message';
    el.appendChild(msg);

    const close = document.createElement('button');
    close.className = 'gsb-close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    Object.assign(close.style, {
        position: 'absolute',
        top: '50%',
        right: '12px',
        transform: 'translateY(-50%)',
        background: 'transparent',
        border: 'none',
        color: 'inherit',
        fontSize: '20px',
        lineHeight: '1',
        cursor: 'pointer',
        padding: '0 4px',
        opacity: '0.85',
    });
    close.addEventListener('click', hideBanner);
    el.appendChild(close);

    document.body.appendChild(el);
    return el;
}

export function hideBanner() {
    const el = document.getElementById(CONTAINER_ID);
    if (!el) return;
    el.style.transform = 'translateY(-100%)';
    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
}

/**
 * Show the top status strip.
 * @param {string} message  Text to display.
 * @param {'error'|'warning'|'success'|'info'} [category='info']
 * @param {number} [duration]  Auto-dismiss after ms. Errors stay until
 *                             dismissed by default; others auto-hide.
 */
export function showBanner(message, category = 'info', duration) {
    const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.info;
    const el = ensureContainer();

    el.style.backgroundColor = style.bg;
    el.style.color = style.fg;
    el.querySelector('.gsb-message').textContent = message;

    // Reveal (force reflow first so the transition runs on repeat calls).
    void el.offsetHeight;
    el.style.transform = 'translateY(0)';

    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
    const ttl = duration != null
        ? duration
        : (category === 'error' ? 8000 : 4000);
    if (ttl > 0) {
        hideTimer = setTimeout(hideBanner, ttl);
    }
}

export default showBanner;
